<template>
  <div class="step-importance">
    <div class="header-row mb-3">
      <div>
        <a-typography-text strong>
          {{ configuredCount }} of {{ steps.length }} steps configured
        </a-typography-text>
        <a-typography-text type="secondary" class="ml-2">
          (unset steps default to
          <a-tag :style="defaultModeTagStyle">{{ defaultModeLabel }}</a-tag
          >)
        </a-typography-text>
      </div>
      <a-space>
        <a-input-search
          v-if="steps.length > 10"
          v-model:value="searchTerm"
          placeholder="Search steps"
          class="search-input"
          allow-clear
        />
        <a-popconfirm
          title="Reset every step to plant defaults and clear all overrides?"
          ok-text="Reset"
          cancel-text="Cancel"
          @confirm="handleResetAll"
        >
          <a-button size="small">Reset all</a-button>
        </a-popconfirm>
      </a-space>
    </div>

    <a-alert
      v-if="!steps.length"
      type="warning"
      show-icon
      message="No steps found for this operation. Add substeps in Define Operation first."
    />

    <a-table
      v-else
      :columns="columns"
      :data-source="filteredSteps"
      :pagination="false"
      :scroll="{ x: 'max-content', y: '48vh' }"
      row-key="index"
      size="middle"
      bordered
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'index'">
          #{{ record.index + 1 }}
        </template>

        <template v-if="column.dataIndex === 'mode'">
          <a-select
            :value="stepConfig(record.index).failure_mode || DEFAULT_STEP_MODE"
            style="width: 100%; min-width: 180px"
            @change="(val) => handleModeChange(record.index, val)"
          >
            <a-select-option
              v-for="mode in FAILURE_MODES"
              :key="mode.id"
              :value="mode.id"
            >
              <a-tag
                :style="{
                  background: mode.bg,
                  color: mode.fg,
                  border: 'none',
                  margin: 0,
                }"
              >
                {{ mode.short }}
              </a-tag>
              <span class="ml-2 text-secondary" style="font-size: 12px">
                {{ mode.description }}
              </span>
            </a-select-option>
          </a-select>
        </template>

        <template v-if="column.dataIndex === 'perOccurrence'">
          <span class="cost-preview">
            {{ formatCurrency(previewCost(record.index)) }}
          </span>
          <a-tag
            v-if="hasOverrides(record.index)"
            color="blue"
            class="ml-1"
            style="font-size: 11px"
          >
            Custom
          </a-tag>
        </template>
      </template>

      <template #expandedRowRender="{ record }">
        <div class="overrides-panel">
          <div class="overrides-header">
            <a-typography-text strong>Per-step overrides</a-typography-text>
            <a-button
              size="small"
              :disabled="!hasOverrides(record.index)"
              @click="handleResetStep(record.index)"
            >
              Reset to plant defaults
            </a-button>
          </div>
          <a-row :gutter="[16, 12]">
            <a-col
              v-for="key in OVERRIDE_KEYS"
              :key="key"
              :xs="24"
              :md="8"
            >
              <div class="override-field">
                <label class="override-label">
                  {{ overrideLabelFor(key) }}
                  <a-tag class="ml-1" style="font-size: 11px">
                    {{ overrideUnitFor(key) }}
                  </a-tag>
                </label>
                <a-input-number
                  :value="stepConfig(record.index)?.overrides?.[key] ?? null"
                  :placeholder="String(economics[key] ?? '')"
                  :disabled="!isRelevant(record.index, key)"
                  :min="0"
                  style="width: 100%"
                  @change="(val) => handleOverride(record.index, key, val)"
                />
                <small class="text-secondary">
                  <template v-if="isRelevant(record.index, key)">
                    Leave empty to use plant default.
                  </template>
                  <template v-else>
                    Not used by
                    {{ modeLabel(stepConfig(record.index).failure_mode) }}.
                  </template>
                </small>
              </div>
            </a-col>
          </a-row>
        </div>
      </template>
    </a-table>
  </div>
</template>

<script>
import {
  DEFAULT_ECONOMICS,
  DEFAULT_STEP_MODE,
  FAILURE_MODES,
  FAILURE_MODE_MAP,
  OVERRIDE_KEYS,
  ECONOMICS_FIELDS,
} from 'src/utils/valueStory/config';
import { costPerOccurrence } from 'src/utils/valueStory/cost';

// Which override fields matter for each failure mode's cost formula.
const MODE_RELEVANCE = {
  scrap: { bomCost: true, reworkMinutes: true, warrantyCost: false },
  field: { bomCost: false, reworkMinutes: true, warrantyCost: true },
  'rework-down': { bomCost: false, reworkMinutes: true, warrantyCost: false },
  'rework-station': { bomCost: false, reworkMinutes: true, warrantyCost: false },
  cosmetic: { bomCost: false, reworkMinutes: true, warrantyCost: false },
  none: { bomCost: false, reworkMinutes: false, warrantyCost: false },
};

export default {
  props: {
    steps: { type: Array, default: () => [] },
    stepsConfig: { type: Object, default: () => ({}) },
    economics: { type: Object, default: () => ({ ...DEFAULT_ECONOMICS }) },
  },
  emits: ['update:stepsConfig'],
  setup() {
    return { FAILURE_MODES, DEFAULT_STEP_MODE, OVERRIDE_KEYS };
  },
  data() {
    return {
      searchTerm: '',
      columns: [
        { title: '#', dataIndex: 'index', width: 60 },
        { title: 'Step', dataIndex: 'name', ellipsis: true },
        { title: 'Failure mode', dataIndex: 'mode', width: 260 },
        { title: 'Per-occurrence', dataIndex: 'perOccurrence', width: 160 },
      ],
    };
  },
  computed: {
    defaultModeLabel() {
      return FAILURE_MODE_MAP[DEFAULT_STEP_MODE]?.short || DEFAULT_STEP_MODE;
    },
    defaultModeTagStyle() {
      const m = FAILURE_MODE_MAP[DEFAULT_STEP_MODE];
      return m
        ? { background: m.bg, color: m.fg, border: 'none', margin: '0 4px' }
        : {};
    },
    configuredCount() {
      return Object.keys(this.stepsConfig || {}).length;
    },
    filteredSteps() {
      if (!this.searchTerm) return this.steps;
      const q = this.searchTerm.toLowerCase();
      return this.steps.filter((s) =>
        (s.name || '').toLowerCase().includes(q)
      );
    },
  },
  methods: {
    stepConfig(index) {
      return this.stepsConfig?.[index] || {};
    },
    hasOverrides(index) {
      const o = this.stepConfig(index).overrides;
      return o && Object.keys(o).length > 0;
    },
    previewCost(index) {
      const cfg = this.stepConfig(index);
      const mode = cfg.failure_mode || DEFAULT_STEP_MODE;
      const overrides = cfg.overrides || {};
      const eff = { ...DEFAULT_ECONOMICS, ...this.economics, ...overrides };
      return costPerOccurrence(mode, eff);
    },
    isRelevant(index, key) {
      const mode = this.stepConfig(index).failure_mode || DEFAULT_STEP_MODE;
      return MODE_RELEVANCE[mode]?.[key] ?? false;
    },
    modeLabel(id) {
      return FAILURE_MODE_MAP[id]?.short || id || 'default mode';
    },
    overrideLabelFor(key) {
      return ECONOMICS_FIELDS.find((f) => f.key === key)?.label || key;
    },
    overrideUnitFor(key) {
      return ECONOMICS_FIELDS.find((f) => f.key === key)?.unit || '';
    },
    formatCurrency(v) {
      if (!Number.isFinite(v)) return '$0';
      return `$${Math.round(v).toLocaleString()}`;
    },
    emitConfig(next) {
      this.$emit('update:stepsConfig', next);
    },
    handleModeChange(index, mode) {
      const next = { ...(this.stepsConfig || {}) };
      const current = { ...(next[index] || {}) };
      current.failure_mode = mode;
      next[index] = current;
      this.emitConfig(next);
    },
    handleOverride(index, key, value) {
      const next = { ...(this.stepsConfig || {}) };
      const current = { ...(next[index] || {}) };
      const overrides = { ...(current.overrides || {}) };
      if (value == null || value === '' || !Number.isFinite(Number(value))) {
        delete overrides[key];
      } else {
        overrides[key] = Number(value);
      }
      if (Object.keys(overrides).length === 0) {
        delete current.overrides;
      } else {
        current.overrides = overrides;
      }
      if (
        !current.failure_mode &&
        (!current.overrides || Object.keys(current.overrides).length === 0)
      ) {
        delete next[index];
      } else {
        next[index] = current;
      }
      this.emitConfig(next);
    },
    handleResetStep(index) {
      const next = { ...(this.stepsConfig || {}) };
      delete next[index];
      this.emitConfig(next);
    },
    handleResetAll() {
      this.emitConfig({});
    },
  },
};
</script>

<style scoped>
.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.search-input {
  width: 220px;
  max-width: 100%;
}
@media (max-width: 640px) {
  .search-input {
    width: 100%;
  }
}
.cost-preview {
  font-weight: 600;
}
.overrides-panel {
  padding: 12px 16px;
  background: #fafafa;
  border-radius: 4px;
}
.overrides-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.override-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.override-label {
  font-weight: 500;
  font-size: 13px;
}
</style>
